import { c as _c } from "react/compiler-runtime";
import { fetchFanList, fetchFanRankCancel, fetchFanRankList, fetchFanRankWrite } from '@module/apis/client-side/fan';
import type { FanRankCancelParams, FanRankListParams, FanRankWriteParams, FetchFanListParams, FetchFanListResponse, FetchFanRankEditResponse, FetchFanRankListResponse } from '@module/apis/client-side/fan/type';
import { keepPreviousData, type UseInfiniteQueryOptions, type UseMutationOptions, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import TimeConstants from '../../utils/item';
import type { CustomUseQueryOptions } from '../type';
import { fanQueryKeys } from './fanQueryKeys';
export const useFetchFanListQuery = (fetchFanListParams, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== fetchFanListParams) {
    t0 = fanQueryKeys.fetchFanList(fetchFanListParams);
    $[0] = fetchFanListParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== fetchFanListParams) {
    t1 = () => fetchFanList(fetchFanListParams);
    $[2] = fetchFanListParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      staleTime: TimeConstants.HALF_SECOND * 10,
      placeholderData: keepPreviousData
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const useFanRankListQuery = (fetchFanRankListParams, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== fetchFanRankListParams) {
    t0 = fanQueryKeys.fetchFanRankList(fetchFanRankListParams);
    $[0] = fetchFanRankListParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== fetchFanRankListParams) {
    t1 = () => fetchFanRankList(fetchFanRankListParams);
    $[2] = fetchFanRankListParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const useFanRankListInfiniteQuery = (params, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== params) {
    t0 = fanQueryKeys.fetchFanRankList(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = t2 => {
      const {
        pageParam: t3
      } = t2;
      const pageParam = t3 === undefined ? 1 : t3;
      const actualPageParam = typeof pageParam === "number" ? pageParam : 1;
      const offset = params.limit * ((actualPageParam || 1) - 1);
      return fetchFanRankList({
        ...params,
        offset
      });
    };
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      initialPageParam: 1,
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      getNextPageParam: _temp2,
      select: _temp4
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useInfiniteQuery(t2);
};
export const useFanRankWriteMutation = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = fanQueryKeys.fetchFanRankWrite();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0.queryKey,
      mutationFn: _temp5,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useFanRankCancelMutation = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = fanQueryKeys.fetchFanRankCancel();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0.queryKey,
      mutationFn: _temp6,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
function _temp(page) {
  return page.list;
}
function _temp2(lastPage, allPages) {
  const totalFetchedItems = allPages.flatMap(_temp).length;
  if (totalFetchedItems < lastPage.page.total) {
    const nextPage = allPages.length + 1;
    return nextPage;
  }
}
function _temp3(page_0) {
  return page_0.list ?? [];
}
function _temp4(data) {
  return {
    pages: data?.pages.flatMap(_temp3),
    pageParams: data.pageParams,
    total: data?.pages.length > 0 ? data?.pages[0].page.total : 0
  };
}
function _temp5(fetchFanRankWriteParams) {
  return fetchFanRankWrite(fetchFanRankWriteParams);
}
function _temp6(fetchFanRankCancelParams) {
  return fetchFanRankCancel(fetchFanRankCancelParams);
}